import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import {
  acceptRecommendation,
  dismissRecommendation,
  fetchDealRecommendations,
  fetchMyRecommendations,
  type Recommendation,
} from './nextStepApi'

/**
 * «Следующий шаг» (тикет #133): что сделать по сделке сейчас и готовый повод для звонка.
 *
 * Чего экран не делает сам:
 * - не решает, есть ли рекомендация: её нашли правила сервера (КП без ответа неделю, застревание
 *   на стадии, молчание), и сверка идёт при каждом открытии;
 * - не выдаёт шаблон за мнение ИИ: источник повода подписан, а если повод — шаблон, причина
 *   («ИИ выключен», «ИИ готовит повод») показана словами сервера;
 * - не решает, можно ли решать: флаги `mayDecide`/`mayAccept` пришли с сервера (область
 *   `sales.deal:update` смотрящего); без них кнопок нет, и сказано, кто решает.
 *
 * Отклонённая рекомендация не возвращается для того же предмета — это держит сервер, экран лишь
 * убирает её из списка.
 */
type Props = { dealId: string } | { dealId?: undefined }

export function NextStepPanel(props: Props) {
  const { t, user } = useApp()
  const onCard = props.dealId != null
  const allowed = can(user, 'sales.deal', 'read')
  const query = useQuery({
    queryKey: ['next-step', props.dealId ?? 'mine'],
    queryFn: ({ signal }) =>
      onCard
        ? fetchDealRecommendations(props.dealId as string, signal)
        : fetchMyRecommendations(signal).then((mine) => ({ recommendations: mine.recommendations, history: [] })),
    retry: false,
    enabled: allowed,
  })
  if (!allowed) return null
  const items = query.data?.recommendations ?? []
  const history = query.data?.history ?? []
  // An empty block is not drawn: "nothing to recommend" every day is noise people learn to skip.
  // An error is said in words — it must not be mistaken for emptiness.
  if (!query.isError && items.length === 0 && history.length === 0) return null
  return (
    <section className="panel" aria-labelledby="next-step-h">
      <div className="panel-h">
        <span className="h2" id="next-step-h">{t('nextStep.title')}</span>
        {query.isSuccess && <span className="count-pill">{items.length}</span>}
      </div>
      <div className="panel-b stack" style={{ gap: 12 }}>
        {query.isError ? (
          <p role="alert" className="t-sm">
            {query.error instanceof ApiError ? query.error.message : t('nextStep.error')}
          </p>
        ) : (
          <>
            {items.map((item) => <RecommendationRow key={item.id} item={item} onCard={onCard} />)}
            {items.length === 0 && <p className="t-sm muted" style={{ margin: 0 }}>{t('nextStep.none')}</p>}
            {history.length > 0 && <History items={history} />}
          </>
        )}
      </div>
    </section>
  )
}

function RecommendationRow({ item, onCard }: { item: Recommendation; onCard: boolean }) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)
  const [dismissing, setDismissing] = useState(false)
  const [reason, setReason] = useState('')

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['next-step'] })
    void queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
  }
  const failure = (error: unknown) => setNotice(error instanceof ApiError ? error.message : t('app.error'))
  const accept = useMutation({
    mutationFn: () => acceptRecommendation(item.id),
    onSuccess: () => {
      setNotice(null)
      refresh()
    },
    onError: failure,
  })
  const dismiss = useMutation({
    mutationFn: (why: string) => dismissRecommendation(item.id, why),
    onSuccess: () => {
      setDismissing(false)
      refresh()
    },
    onError: failure,
  })
  const busy = accept.isPending || dismiss.isPending

  return (
    <article className="stack" style={{ gap: 4 }} aria-label={item.action}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <span className="strong">{item.action}</span>
        <span className="chip">{t(`nextStep.kind.${item.kind}`)}</span>
        {!onCard && (
          <Link className="t-sm" to={`/deals/${item.dealId}`}>
            {t('nextStep.deal', { number: item.dealNumber })}
            {item.counterpartyName != null ? ` · ${item.counterpartyName}` : ''}
          </Link>
        )}
      </div>
      <span className="t-sm secondary">{item.situation}</span>
      <div className="stack" style={{ gap: 2 }}>
        <span className="t-sm">
          <span className="strong">{t('nextStep.reason')}</span> {item.reason.text}
        </span>
        {item.reason.opener != null && (
          <span className="t-sm">{t('nextStep.opener', { text: item.reason.opener })}</span>
        )}
        <span className="t-sm muted" data-testid="next-step-source">
          {item.reason.source === 'ai'
            ? t('nextStep.source.ai')
            : (item.reason.note ?? t('nextStep.source.template'))}
        </span>
      </div>
      {notice && <p role="alert" className="t-sm" style={{ margin: 0 }}>{notice}</p>}
      {!item.mayDecide ? (
        <span className="t-sm muted">
          {item.responsibleName != null
            ? t('nextStep.decidedBy', { name: item.responsibleName })
            : t('nextStep.notYours')}
        </span>
      ) : dismissing ? (
        <form
          className="row"
          style={{ gap: 6, flexWrap: 'wrap' }}
          onSubmit={(event) => {
            event.preventDefault()
            dismiss.mutate(reason)
          }}
        >
          <input
            className="input"
            aria-label={t('nextStep.dismissReason')}
            placeholder={t('nextStep.dismissReason')}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <button type="submit" className="btn btn-secondary btn-sm" disabled={busy}>{t('nextStep.dismissConfirm')}</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDismissing(false)}>
            {t('nextStep.cancel')}
          </button>
        </form>
      ) : (
        <div className="row" style={{ gap: 6 }}>
          {item.mayAccept && (
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => accept.mutate()}>
              {t('nextStep.accept')}
            </button>
          )}
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setDismissing(true)}>
            {t('nextStep.dismiss')}
          </button>
        </div>
      )}
    </article>
  )
}

/** The decisions taken on this deal: the record of what was accepted and dismissed. */
function History({ items }: { items: Recommendation[] }) {
  const { t, locale } = useApp()
  return (
    <details>
      <summary className="t-sm">{t('nextStep.history', { count: items.length })}</summary>
      <ul className="stack t-sm" style={{ gap: 2, margin: '6px 0 0', paddingLeft: 16 }}>
        {items.map((item) => (
          <li key={item.id}>
            {item.action} — {t(`nextStep.status.${item.status}`)}
            {item.decidedAt != null ? ` ${new Date(item.decidedAt).toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU')}` : ''}
            {item.decisionNote != null ? ` (${item.decisionNote})` : ''}
          </li>
        ))}
      </ul>
    </details>
  )
}
